import { useEffect, useState } from 'react';
import remove from 'lodash/remove';

import { Headers } from './headers';
import { Board, CardData } from './board';
import { QuizQuestions } from 'graphql/queries/types/QuizQuestions';
import { Team, TeamShipHealth, SharpshooterUserSessionAdditionalInfo } from './sharpshooter-types';
import { SharpshooterEndScreen } from './sharpshooter-end';
import { generateGridData } from './grid-generator';
import { useQuizSessionContext } from 'contexts';

enum Scenes {
    Quiz,
    EndScreen
}
interface Props {
    quizQuestions: QuizQuestions;
    gridColumns: number;
    gridRows: number;
    teams: Team[];
    eventLogo?: string | null;
    isSinglePlayer?: boolean;
}
/**
 * TODO - would be nice - rehidrate board status from user session
 */
export const SharpshooterQuiz = (props: Props) => {
    const sessionContext = useQuizSessionContext();
    const [currentScene, setCurrentScene] = useState(Scenes.Quiz);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [incorrectAnswers, setIncorrectAnswers] = useState(0);
    const [teamShips, setTeamShips] = useState<TeamShipHealth[]>(
        props.teams.map((t) => ({
            ...t,
            ships: t.shipLocations.length,
            destroyed: 0
        }))
    );

    useEffect(() => {
        if (sessionContext?.userSession?.hasEnded) {
            setCurrentScene(Scenes.EndScreen);
        }
    }, [sessionContext?.userSession?.hasEnded]);

    const handleCorrectAnswer = (question: CardData) => {
        setCorrectAnswers(correctAnswers + 1);

        // Check for quiz end
        let quizEnded = false;
        if (question.shipTeam) {
            const newTeamShipStatus = [...teamShips];
            const team = remove(newTeamShipStatus, (s) => s.name === question.shipTeam)[0];
            team.destroyed = team.destroyed + 1;
            newTeamShipStatus.push(team);
            setTeamShips(newTeamShipStatus);

            // Check for quiz end
            const inPlayTeams = newTeamShipStatus.filter((t) => t.ships !== t.destroyed);
            if (props.isSinglePlayer) {
                if (inPlayTeams.length === 0) {
                    quizEnded = true;
                }
            } else if (inPlayTeams.length === 1) {
                quizEnded = true;
            }
        }

        if (sessionContext) {
            const additionalInfo: SharpshooterUserSessionAdditionalInfo = sessionContext.userSession.additionalInfo ?? {};
            const answeredCards = additionalInfo?.answeredCards ? [...additionalInfo.answeredCards] : [];
            answeredCards.push({
                locationCode: question.points,
                correct: true
            });
            additionalInfo.answeredCards = answeredCards;

            if (question.shipTeam) {
                const shipStatus = additionalInfo.shipStatus ? [...additionalInfo.shipStatus] : [];
                const removedTeams = remove(shipStatus, (ss) => ss.teamName === question.shipTeam);
                const team = removedTeams.length > 0 ? removedTeams[0] : { sunkShips: 0, teamName: question.shipTeam };
                team.sunkShips = team.sunkShips + 1;
                additionalInfo.shipStatus = [...shipStatus, team];
            }

            sessionContext.updateUserSession({
                answersAttempted: sessionContext.userSession.answersAttempted + 1,
                correctAnswers: sessionContext.userSession.correctAnswers + 1,
                id: sessionContext.userSession.id,
                currentTime: new Date().toISOString(),
                hasEnded: quizEnded,
                additionalInfo
            });
        }

        if (quizEnded) {
            setCurrentScene(Scenes.EndScreen);
        }
    };

    const handleIncorrectAnswer = (question: CardData) => {
        setIncorrectAnswers(incorrectAnswers + 1);

        if (sessionContext) {
            sessionContext.updateUserSession({
                answersAttempted: sessionContext.userSession.answersAttempted + 1,
                correctAnswers: sessionContext.userSession.correctAnswers,
                id: sessionContext.userSession.id,
                currentTime: new Date().toISOString(),
                additionalInfo: sessionContext.userSession.additionalInfo
            });
        }
    };

    const onExitQuiz = async () => {
        if (sessionContext) {
            const updatedSessionData = {
                answersAttempted: sessionContext.userSession.answersAttempted + 1,
                correctAnswers: sessionContext.userSession.correctAnswers + 1,
                id: sessionContext.userSession.id,
                currentTime: new Date().toISOString(),
                hasEnded: true,
                additionalInfo: sessionContext.userSession.additionalInfo
            };
            console.log("save end use session - now", updatedSessionData);
            await sessionContext.updateUserSession(updatedSessionData);
            console.log("save end use session - saved")
        }
        setCurrentScene(Scenes.EndScreen);
    };

    const gridDataWithTeamNames = generateGridData(props.gridColumns, props.gridRows);
    props.teams.forEach((team) => {
        team.shipLocations.forEach((ship) => {
            gridDataWithTeamNames[ship.columnIndex].cardRows[ship.rowIndex].shipTeam = team.name;
        });
    });

    const scenes = {
        [Scenes.Quiz]: () => (
            <div className="battleship-quiz">
                <Headers
                    title={props.quizQuestions.quiz_master_quizzes_by_pk!.name}
                    eventLogo={props.eventLogo}
                    correctAnswers={correctAnswers}
                    incorrectAnswers={incorrectAnswers}
                    teamShipStatus={teamShips}
                    quizTimelimitSeconds={sessionContext?.session.maxDurationSec}
                    onQuizExit={onExitQuiz}
                />
                <Board
                    eventLogo={props.eventLogo}
                    gridData={gridDataWithTeamNames}
                    questions={props.quizQuestions.quiz_master_quizzes_by_pk?.questions!}
                    isSinglePlayer={!!props.isSinglePlayer}
                    maxQuestionDuration={sessionContext?.session.questionTimeSec}
                    onCorrectAnswer={handleCorrectAnswer}
                    onIncorrectAnswer={handleIncorrectAnswer}
                />
            </div>
        ),
        [Scenes.EndScreen]: () => (
            <SharpshooterEndScreen
                correctAnswers={correctAnswers}
                incorrectAnswers={incorrectAnswers}
                teamShipStatus={teamShips}
                isSinglePlayer={!!props.isSinglePlayer}
            />
        )
    };

    return scenes[currentScene]();
};
